import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, ME_KEY } from '../Authentication';

/**
 * Conexão em tempo real (Socket.IO em /realtime) — autenticada pelo cookie de sessão.
 * Reconecta quando o usuário muda. Use o hook useSocketEvent(evento, handler) nos módulos.
 */
const Ctx = createContext<Socket | null>(null);

export function SocketConnectionProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const s = io({ path: '/realtime', withCredentials: true, transports: ['websocket', 'polling'] });
    setSocket(s);
    // nova notificação → atualiza contador e lista
    s.on('notification', () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ME_KEY });
    });
    return () => { s.disconnect(); };
  }, [me?.id, qc]);

  return <Ctx.Provider value={socket}>{children}</Ctx.Provider>;
}

export const useSocket = () => useContext(Ctx);

/** Assina um evento do servidor enquanto o componente estiver montado. */
export function useSocketEvent<T = unknown>(event: string, handler: (data: T) => void) {
  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;
    socket.on(event, handler);
    return () => { socket.off(event, handler); };
  }, [socket, event, handler]);
}
